import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu, X, LogOut, CreditCard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import UserMenu from './UserMenu';
import ThemeToggle from './ThemeToggle';
import { color, gradient, radius } from '../../config/tokens';

export const Header = ({ currentPath }) => {
  const { user, subscription, openAuthModal, handleSignOut } = useAuth();
  const tier = subscription?.tier || 'free';
  const isPremium = tier === 'pro' || tier === 'team';
  const navigate = useNavigate();
  const isHome = currentPath === '/';
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu on navigation and on Escape.
  useEffect(() => { setMenuOpen(false); }, [currentPath]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const navLink = (active) => ({
    background: 'none',
    border: 'none',
    color: active ? color.accent.light : color.text.muted,
    cursor: 'pointer',
    fontSize: '0.875rem',
    textDecoration: 'none'
  });

  const drawerRow = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.875rem 0.75rem',
    minHeight: '44px',
    boxSizing: 'border-box',
    background: 'none',
    border: 'none',
    borderRadius: radius.md,
    color: active ? color.accent.light : color.text.secondary,
    cursor: 'pointer',
    fontSize: '1rem',
    textDecoration: 'none',
    textAlign: 'left'
  });

  return (
    <header style={{
      borderBottom: `1px solid ${color.border.subtle}`,
      backgroundColor: color.bg.surface,
      padding: '1rem'
    }} className="header-container">
      <div style={{
        maxWidth: '80rem',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Left: Logo/Title */}
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          {!isHome && (
            <button
              onClick={() => navigate('/')}
              aria-label="Go back to home"
              className="header-back-btn"
              style={{
                background: 'none',
                border: 'none',
                color: color.text.muted,
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                borderRadius: radius.md,
                flexShrink: 0
              }}
            >
              <ArrowLeft size={20} aria-hidden="true" />
            </button>
          )}
          <Link
            to="/"
            className="header-logo-link"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              textDecoration: 'none',
              minWidth: 0
            }}
          >
            <img
              src={process.env.PUBLIC_URL + '/logo-icon.png'}
              alt=""
              aria-hidden="true"
              className="header-logo-icon"
              style={{
                width: '40px',
                height: '40px',
                flexShrink: 0
              }}
            />
            <div style={{ minWidth: 0 }}>
              <h1 className="header-title" style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: color.text.heading,
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                The Thinking Council
              </h1>
              <p className="header-subtitle" style={{
                fontSize: '0.75rem',
                color: color.text.subtle,
                margin: 0
              }}>
                Multi-perspective AI discussion
              </p>
            </div>
          </Link>
        </div>

        {/* Right: desktop nav */}
        <nav className="header-nav" aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/about" className="header-nav-link" aria-current={currentPath === '/about' ? 'page' : undefined} style={navLink(currentPath === '/about')}>
            About
          </Link>
          <Link to="/pricing" className="header-nav-link" aria-current={currentPath === '/pricing' ? 'page' : undefined} style={navLink(currentPath === '/pricing')}>
            Pricing
          </Link>
          {user && isPremium && (
            <Link to="/history" className="header-nav-link" aria-current={currentPath === '/history' ? 'page' : undefined} style={navLink(currentPath === '/history')}>
              History
            </Link>
          )}

          <ThemeToggle />

          {user ? (
            <UserMenu />
          ) : (
            <button
              className="header-signin-btn"
              onClick={openAuthModal}
              style={{
                padding: '0.5rem 1rem',
                background: gradient.primary,
                color: 'white',
                borderRadius: radius.md,
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Sign In
            </button>
          )}
        </nav>

        {/* Right: mobile hamburger */}
        <button
          className="header-hamburger tc-btn"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          style={{
            background: 'none',
            border: `1px solid ${color.border.default}`,
            borderRadius: radius.md,
            color: color.text.muted,
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          id="mobile-menu"
          className="header-mobile-drawer"
          style={{
            maxWidth: '80rem',
            margin: '0.75rem auto 0',
            gap: '0.125rem',
            borderTop: `1px solid ${color.border.subtle}`,
            paddingTop: '0.5rem'
          }}
        >
          <Link to="/about" style={drawerRow(currentPath === '/about')} aria-current={currentPath === '/about' ? 'page' : undefined}>About</Link>
          <Link to="/pricing" style={drawerRow(currentPath === '/pricing')} aria-current={currentPath === '/pricing' ? 'page' : undefined}>Pricing</Link>
          {user && isPremium && (
            <Link to="/history" style={drawerRow(currentPath === '/history')} aria-current={currentPath === '/history' ? 'page' : undefined}>History</Link>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', borderTop: `1px solid ${color.border.subtle}`, marginTop: '0.25rem' }}>
            <span style={{ color: color.text.secondary, fontSize: '1rem' }}>Theme</span>
            <ThemeToggle />
          </div>

          <div style={{ borderTop: `1px solid ${color.border.subtle}`, marginTop: '0.25rem', paddingTop: '0.5rem' }}>
            {user ? (
              <>
                <div style={{ padding: '0.25rem 0.75rem 0.5rem' }}>
                  <div style={{ color: color.text.primary, fontSize: '0.9375rem', fontWeight: 500 }}>{user.displayName || user.email?.split('@')[0]}</div>
                  <div style={{ color: color.text.subtle, fontSize: '0.8125rem' }}>{user.email}</div>
                </div>
                <button onClick={() => { navigate('/account'); setMenuOpen(false); }} style={drawerRow(false)}>
                  <CreditCard size={16} aria-hidden="true" />
                  Account &amp; billing
                </button>
                <button onClick={() => { handleSignOut(); setMenuOpen(false); }} style={{ ...drawerRow(false), color: color.status.danger }}>
                  <LogOut size={16} aria-hidden="true" />
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => { openAuthModal(); setMenuOpen(false); }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: gradient.primary,
                  color: 'white',
                  borderRadius: radius.md,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
